import uuid from 'node-uuid';
import moment from 'moment';
import { get, last, head } from 'lodash';
import { RSAA } from 'redux-api-middleware';
import endpoints from '../../utils/endpoints';
import { getIFTPayments, getIsEditing, isResponseMaxTransactionLimitError } from './selectors';
import {
	getIFTPaymentReviewRequest,
	getIFTPaymentConfirmedRequest,
	getIFTEditPaymentRequest,
} from './iftRequestBuilder';
import { pages, goToPage } from '../page/actions';
import { getPayeeByIBANOrAccountNumber } from '../payees/selectors';
import { getPayeeById } from '../commonSelectors/selectors';
import { getAccountCurrency, isKapitiAccount } from '../accounts/selectors';
import { getUserId } from '../user/selectors';
import { isFromAccountExoticCurrency } from '../../helpers/fieldHelpers';
import { CONST_INSTRUCTED, CONST_EQUIVALENT } from '../../config/constants';
import currencyMapping from '../../config/currencyMapping';
import { isConflictAndDailyLimitError } from '../../helpers/apiResponse';
import { getMissingDetailsForChapsAndIFTEditPayment } from '../../utils/LCVMissingDetailsUtils';

export const IFT_ADD_PAYMENTS = 'IFT/ADD_PAYMENTS';
export const IFT_SELECT_PAYMENT = 'IFT/SELECT_PAYMENT';
export const IFT_DELETE_PAYMENT = 'IFT/DELETE_PAYMENT';
export const IFT_UPDATE_PAYMENT = 'IFT/UPDATE_PAYMENT';
export const IFT_CLEAR_SELECTED_PAYMENT = 'IFT/CLEAR_SELECTED_PAYMENT';
export const IFT_UPDATE_SEND_AMOUNT_CURRENCY = 'IFT/UPDATE_SEND_AMOUNT_CURRENCY';

export const SUBMIT_IFT_PAYMENT_REVIEW_REQUEST = 'SUBMIT_IFT_PAYMENT_REVIEW_REQUEST';
export const SUBMIT_IFT_PAYMENT_REVIEW_SUCCESS = 'SUBMIT_IFT_PAYMENT_REVIEW_SUCCESS';
export const SUBMIT_IFT_PAYMENT_REVIEW_FAILURE = 'SUBMIT_IFT_PAYMENT_REVIEW_FAILURE';
export const IFT_UPDATE_SELECTED_PAYMENT_DETAILS = 'IFT/UPDATE_SELECTED_PAYMENT_DETAILS';

export const SUBMIT_GET_IFT_PAYMENT_TO_EDIT_REQUEST = 'SUBMIT_GET_IFT_PAYMENT_TO_EDIT_REQUEST';
export const SUBMIT_GET_IFT_PAYMENT_TO_EDIT_SUCCESS = 'SUBMIT_GET_IFT_PAYMENT_TO_EDIT_SUCCESS';
export const SUBMIT_GET_IFT_PAYMENT_TO_EDIT_FAILURE = 'SUBMIT_GET_IFT_PAYMENT_TO_EDIT_FAILURE';

export const SUBMIT_IFT_PAYMENT_CONFIRMED_REQUEST = 'SUBMIT_IFT_PAYMENT_CONFIRMED_REQUEST';
export const SUBMIT_IFT_PAYMENT_CONFIRMED_SUCCESS = 'SUBMIT_IFT_PAYMENT_CONFIRMED_SUCCESS';
export const SUBMIT_IFT_PAYMENT_CONFIRMED_FAILURE = 'SUBMIT_IFT_PAYMENT_CONFIRMED_FAILURE';

export const SUBMIT_IFT_EDIT_PAYMENT_REQUEST = 'SUBMIT_IFT_EDIT_PAYMENT_REQUEST';
export const SUBMIT_IFT_EDIT_PAYMENT_SUCCESS = 'SUBMIT_IFT_EDIT_PAYMENT_SUCCESS';
export const SUBMIT_IFT_EDIT_PAYMENT_FAILURE = 'SUBMIT_IFT_EDIT_PAYMENT_FAILURE';

export const IFT_UPDATE_PRE_BOOKED_CHECKED = 'IFT/UPDATE_PRE_BOOKED_CHECKED';
export const IFT_SET_PAYMENT_TO_EDIT = 'IFT/SET_PAYMENT_TO_EDIT';
export const IFT_SET_AUTHORISERS = 'IFT/SET_AUTHORISERS';

export const SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_REQUEST = 'SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_REQUEST';
export const SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS = 'SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS';
export const SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_FAILURE = 'SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_FAILURE';

export const RESET_IFT_DAILY_LIMIT_AUTHORISATION = 'IFT/RESET_IFT_DAILY_LIMIT_AUTHORISATION';

export const setSendCurrency = (state, fromAccount, default_currency) => {
	const fromCurrency = getAccountCurrency(state, fromAccount);
	return isFromAccountExoticCurrency(fromCurrency) ? fromCurrency : default_currency;
};

export const iftAddSinglePayment =
	({ fromAccount }) =>
	(dispatch) =>
		dispatch({
			type: IFT_ADD_PAYMENTS,
			payments: [
				{
					id: uuid.v1(),
					fromAccount,
					urgent: true,
					date: moment(),
					chargingModal: 'SHAR',
					isSendAmountFromDebitCurrency: false,
					sendAmount: '',
					preBookedReference: '',
					exchangeRate: '',
					isDefaultCurrency: false,
				},
			],
		});

export const iftAddPayments =
	({ fromAccount, toAccounts }) =>
	(dispatch, getState) => {
		const state = getState();

		return dispatch({
			type: IFT_ADD_PAYMENTS,
			payments: toAccounts.map((toAccount) => {
				const { default_currency = '', reference, reference_high_value } = getPayeeById(state, toAccount);
				const sendCurrency = setSendCurrency(state, fromAccount, default_currency);
				const fromCurrency = getAccountCurrency(state, fromAccount);
				const currency = currencyMapping.find(
					(currency) => currency.from_currency === fromCurrency && currency.to_currency === sendCurrency
				);
				const isPreBookedRate = get(currency, 'pre_booked_rate_mandatory', '') === 'Y';

				return {
					id: uuid.v1(),
					fromAccount,
					toAccount,
					sendCurrency,
					reason: reference_high_value || reference,
					urgent: true,
					date: moment(),
					chargingModal: 'SHAR',
					isSendAmountFromDebitCurrency: false,
					sendAmount: '',
					preBookedReference: '',
					exchangeRate: '',
					isDefaultCurrency: false,
					isPreBookedRate,
				};
			}),
		});
	};

export const iftSelectPayment = (paymentId, isPaymentListEdit = false) => ({
	type: IFT_SELECT_PAYMENT,
	paymentId,
	isPaymentListEdit,
});

export const iftUpdatePayment = (payment) => ({
	type: IFT_UPDATE_PAYMENT,
	payment,
});

export const iftDeletePayment = (paymentId) => ({
	type: IFT_DELETE_PAYMENT,
	paymentId,
});

export const iftReviewPayments = (payments, userId) => {
	const body = JSON.stringify(payments);
	const endpoint = endpoints.multipart.uri;

	return {
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [
				SUBMIT_IFT_PAYMENT_REVIEW_REQUEST,
				{
					type: SUBMIT_IFT_PAYMENT_REVIEW_SUCCESS,
					meta: { userId },
				},
				SUBMIT_IFT_PAYMENT_REVIEW_FAILURE,
			],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

export const iftConfirmPayments = (payments) => {
	const body = JSON.stringify(payments);
	const endpoint = endpoints.multipart.uri;

	return {
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [
				SUBMIT_IFT_PAYMENT_CONFIRMED_REQUEST,
				SUBMIT_IFT_PAYMENT_CONFIRMED_SUCCESS,
				SUBMIT_IFT_PAYMENT_CONFIRMED_FAILURE,
			],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

export const iftEditPayment = (payment, editPaymentRequestBody) => {
	const body = JSON.stringify(editPaymentRequestBody);
	const endpoint = endpoints.editInternationalPayment.resolve(null, null, payment.fromAccount, payment.context_id);

	return {
		[RSAA]: {
			endpoint,
			method: 'PUT',
			types: [SUBMIT_IFT_EDIT_PAYMENT_REQUEST, SUBMIT_IFT_EDIT_PAYMENT_SUCCESS, SUBMIT_IFT_EDIT_PAYMENT_FAILURE],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

export const setIFTPaymentAuthorisers = (authorisers, paymentId) => ({
	type: IFT_SET_AUTHORISERS,
	paymentId,
	authorisers,
});

export const submitIFTPaymentsForDailyLimitReview = (payments) => {
	const body = JSON.stringify(payments);
	const endpoint = endpoints.multipart.uri;

	return {
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [
				SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_REQUEST,
				SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS,
				SUBMIT_IFT_PAYMENT_DAILY_LIMIT_REVIEW_FAILURE,
			],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

const getPaymentForDailyLimitCheck = (paymentReviewRequestBody) => {
	const dailyLimitAmount = paymentReviewRequestBody.batch_list.reduce(
		(
			sum,
			{
				body: {
					amount: { value },
				},
			}
		) => sum + Number(value),
		0
	);
	const paymentForDailyLimit = paymentReviewRequestBody.batch_list[0];
	paymentForDailyLimit.body.amount.value = dailyLimitAmount;
	return paymentForDailyLimit;
};

export const submitIFTPaymentsForReview = (dispatch, getState) => {
	const state = getState();

	const { bankId } = state.session;
	const userId = getUserId(state);
	const payments = getIFTPayments(state);
	const getApiResponses = (response) => get(response, 'payload.apiResponses', []);

	const paymentReviewRequestBody = {
		batch_list: payments.map((payment) => {
			const isFromKapitiAccount = isKapitiAccount(state, payment.fromAccount);
			return getIFTPaymentReviewRequest(bankId)(payment, isFromKapitiAccount);
		}),
	};
	return dispatch(
		submitIFTPaymentsForDailyLimitReview({
			batch_list: [{ ...getPaymentForDailyLimitCheck(paymentReviewRequestBody) }],
		})
	).then((response) => {
		const apiResponses = getApiResponses(response);
		if (!apiResponses.some(isConflictAndDailyLimitError)) {
			return dispatch(iftReviewPayments(paymentReviewRequestBody, userId)).then((response) => {
				if (response.type === SUBMIT_IFT_PAYMENT_REVIEW_SUCCESS) {
					const apiResponses = getApiResponses(response);
					const isMaxTransactionLimitError = apiResponses.some((response) =>
						isResponseMaxTransactionLimitError(response)
					);
					if (isMaxTransactionLimitError) return null;
					return dispatch(goToPage(pages.IFTReview));
				}
				return null;
			});
		}
		return null;
	});
};

export const resetIFTDailyLimitAuthorisation = () => ({ type: RESET_IFT_DAILY_LIMIT_AUTHORISATION });

export const iftUpdateSelectedPaymentDetails = (key, value) => ({
	type: IFT_UPDATE_SELECTED_PAYMENT_DETAILS,
	key,
	value,
});

export const iftUpdatePreBookedChecked = (isPreBooked) => ({
	type: IFT_UPDATE_PRE_BOOKED_CHECKED,
	isPreBooked,
});

export const continueToPaymentDetails = (dispatch, getState) => {
	const state = getState();
	const payments = get(state, 'ift.payments', []);
	const lastPaymentId = get(last(payments), 'id');
	dispatch(iftSelectPayment(lastPaymentId));

	return dispatch(goToPage(pages.IFTPaymentDetails));
};

export const iftClearSelectedPayment = () => ({
	type: IFT_CLEAR_SELECTED_PAYMENT,
});

export const submitIFTConfirmedPayments = (dispatch, getState) => {
	const state = getState();

	const { bankId } = state.session;
	const isEditing = getIsEditing(state);
	const payments = getIFTPayments(state);

	if (isEditing) {
		const payment = head(payments);
		const editPaymentRequestBody = getIFTEditPaymentRequest(payment);
		return dispatch(iftEditPayment(payment, editPaymentRequestBody)).then(() => dispatch(goToPage(pages.IFTSubmitted)));
	}

	const paymentsConfirmedRequestBody = { batch_list: payments.map(getIFTPaymentConfirmedRequest(bankId)) };

	return dispatch(iftConfirmPayments(paymentsConfirmedRequestBody)).then((action) => {
		// Only proceed to submitted screen if request is successful. Payment error modal displayed via states/page reducer for _FAILURE action.
		if (action.type === SUBMIT_IFT_PAYMENT_CONFIRMED_SUCCESS) {
			dispatch(goToPage(pages.IFTSubmitted));
		}
	});
};

export const iftUpdateSendAmountCurrency = (isSendAmountFromDebitCurrency) => ({
	type: IFT_UPDATE_SEND_AMOUNT_CURRENCY,
	isSendAmountFromDebitCurrency,
});

export const getIFTPaymentToEdit = ({ paymentId, account }) => {
	const endpoint = endpoints.getPaymentById;
	const uri = endpoint.resolve(null, null, account, paymentId);
	return {
		[RSAA]: {
			endpoint: uri,
			method: endpoint.method,
			types: [
				SUBMIT_GET_IFT_PAYMENT_TO_EDIT_REQUEST,
				SUBMIT_GET_IFT_PAYMENT_TO_EDIT_SUCCESS,
				SUBMIT_GET_IFT_PAYMENT_TO_EDIT_FAILURE,
			],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
		},
	};
};

export const iftSetPaymentToEdit = (payment) => ({
	type: IFT_SET_PAYMENT_TO_EDIT,
	payment,
});

export const getPaymentFromResponse = (response = {}, state, { account }) => {
	const instructed = get(response, 'international_amount', []).find((item) => item.type === CONST_INSTRUCTED);
	const equivalent = get(response, 'international_amount', []).find((item) => item.type === CONST_EQUIVALENT);
	const sendAmount = instructed ? `${instructed.value}` : '';

	const toAccount = getPayeeByIBANOrAccountNumber(
		state,
		get(response, 'to_account.account_number'),
		get(response, 'to_account.account_number')
	);
	const fromAccountCurrency = getAccountCurrency(state, account);
	const isNativeCurrency = get(equivalent, 'currency', '') !== get(instructed, 'currency', '');
	const isSendAmountFromDebitCurrency = fromAccountCurrency === get(instructed, 'currency', '') && isNativeCurrency;
	const sendCurrency = isSendAmountFromDebitCurrency
		? get(equivalent, 'currency', '')
		: get(instructed, 'currency', '');

	return {
		id: uuid.v1(),
		context_id: response.id,
		fromAccount: account,
		toAccount: get(toAccount, 'id', ''),
		sendCurrency,
		reason: response.reference,
		urgent: response.urgent || false,
		date: moment(response.submission_date).isSameOrAfter(moment()) ? moment(response.submission_date) : moment(),
		chargingModal: response.charging_model,
		isSendAmountFromDebitCurrency,
		sendAmount,
		isPreBookedRate: response.deal_type === 'PRE_BOOKED_RATE',
		preBookedReference: response.deal_reference,
		exchangeRate: get(response, 'indicative_fx.rate', '').toString(),
	};
};

export const editIFTPayment = (paymentToEdit) => (dispatch, getState) =>
	dispatch(getIFTPaymentToEdit(paymentToEdit)).then((response) =>
		dispatch(getMissingDetailsForChapsAndIFTEditPayment(response.payload)).then(() => {
			const state = getState();
			const payment = getPaymentFromResponse(get(response, 'payload'), state, paymentToEdit);
			dispatch(iftSetPaymentToEdit(payment));
			dispatch(goToPage(pages.IFTPaymentDetails));
		})
	);
